import React, { useState } from 'react';
import { X, ThumbsUp, BookOpen, ChevronLeft } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useTheme, getThemeClasses } from '../context/ThemeContext';
import { sendEmail } from '../services/email';

interface FeedbackModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function FeedbackModal({ isOpen, onClose }: FeedbackModalProps) {
  const { theme } = useTheme();
  const themeClasses = getThemeClasses(theme);
  
  const [feedbackType, setFeedbackType] = useState<'feedback' | 'recommendation' | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    bookTitle: '',
    bookAuthor: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Save to database
      const { error } = await supabase
        .from(feedbackType === 'feedback' ? 'user_feedback' : 'book_recommendations')
        .insert([
          feedbackType === 'feedback'
            ? {
                name: formData.name,
                email: formData.email,
                message: formData.message,
                type: feedbackType,
                status: 'new'
              }
            : {
                name: formData.name,
                email: formData.email,
                message: formData.message,
                book_title: formData.bookTitle,
                book_author: formData.bookAuthor,
                type: feedbackType,
                status: 'new'
              },
        ]);

      if (error) throw error;

      // Send email notification
      await sendEmail({
        name: formData.name,
        email: formData.email,
        message: formData.message,
        type: feedbackType || 'feedback',
        book_title: formData.bookTitle,
        book_author: formData.bookAuthor
      });

      setSubmitStatus('success');
      setTimeout(() => {
        onClose();
        setFeedbackType(null);
        setFormData({
          name: '',
          email: '',
          message: '',
          bookTitle: '',
          bookAuthor: '',
        });
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    setFeedbackType(null);
    setFormData({
      name: '',
      email: '',
      message: '',
      bookTitle: '',
      bookAuthor: '',
    });
    setSubmitStatus('idle');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className={`${themeClasses.dropdown} rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto`}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className={`text-2xl font-bold ${themeClasses.text}`}>
              {feedbackType === null
                ? 'How can we help?'
                : feedbackType === 'feedback'
                ? 'Share Your Feedback'
                : 'Recommend a Book'}
            </h2>
            <button
              onClick={onClose}
              className={`${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors`}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {feedbackType === null ? (
            <div className="grid grid-cols-1 gap-4">
              <button
                onClick={() => setFeedbackType('feedback')}
                className={`p-4 border-2 ${themeClasses.border} rounded-lg hover:${themeClasses.highlight} 
                         transition-all duration-200 flex items-center group`}
              >
                <ThumbsUp className={`h-5 w-5 ${themeClasses.icon} mr-3 group-hover:scale-110 transition-transform`} />
                <div className="text-left">
                  <h3 className={`font-semibold ${themeClasses.text}`}>Share Feedback</h3>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    Tell us about your experience with Book Bliss
                  </p>
                </div>
              </button>

              <button
                onClick={() => setFeedbackType('recommendation')}
                className={`p-4 border-2 ${themeClasses.border} rounded-lg hover:${themeClasses.highlight} 
                         transition-all duration-200 flex items-center group`}
              >
                <BookOpen className={`h-5 w-5 ${themeClasses.icon} mr-3 group-hover:scale-110 transition-transform`} />
                <div className="text-left">
                  <h3 className={`font-semibold ${themeClasses.text}`}>Recommend a Book</h3>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>
                    Share a book you think others would enjoy
                  </p>
                </div>
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <button
                type="button"
                onClick={handleBack}
                className={`flex items-center ${themeClasses.textSecondary} hover:${themeClasses.text} transition-colors mb-4`}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Back to options
              </button>

              <div>
                <label htmlFor="name" className={`block text-sm font-medium ${themeClasses.text}`}>
                  Your Name
                </label>
                <input
                  type="text"
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className={`mt-1 block w-full rounded-md ${themeClasses.border} shadow-sm focus:${themeClasses.highlight} 
                           focus:ring-${themeClasses.primary} sm:text-sm ${themeClasses.dropdown} ${themeClasses.text}`}
                />
              </div>

              <div>
                <label htmlFor="email" className={`block text-sm font-medium ${themeClasses.text}`}>
                  Your Email
                </label>
                <input
                  type="email"
                  id="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`mt-1 block w-full rounded-md ${themeClasses.border} shadow-sm focus:${themeClasses.highlight} 
                           focus:ring-${themeClasses.primary} sm:text-sm ${themeClasses.dropdown} ${themeClasses.text}`}
                />
              </div>

              {feedbackType === 'recommendation' && (
                <>
                  <div>
                    <label htmlFor="bookTitle" className={`block text-sm font-medium ${themeClasses.text}`}>
                      Book Title
                    </label>
                    <input
                      type="text"
                      id="bookTitle"
                      required
                      value={formData.bookTitle}
                      onChange={(e) => setFormData({ ...formData, bookTitle: e.target.value })}
                      className={`mt-1 block w-full rounded-md ${themeClasses.border} shadow-sm focus:${themeClasses.highlight} 
                               focus:ring-${themeClasses.primary} sm:text-sm ${themeClasses.dropdown} ${themeClasses.text}`}
                    />
                  </div>

                  <div>
                    <label htmlFor="bookAuthor" className={`block text-sm font-medium ${themeClasses.text}`}>
                      Author
                    </label>
                    <input
                      type="text"
                      id="bookAuthor"
                      required
                      value={formData.bookAuthor}
                      onChange={(e) => setFormData({ ...formData, bookAuthor: e.target.value })}
                      className={`mt-1 block w-full rounded-md ${themeClasses.border} shadow-sm focus:${themeClasses.highlight} 
                               focus:ring-${themeClasses.primary} sm:text-sm ${themeClasses.dropdown} ${themeClasses.text}`}
                    />
                  </div>
                </>
              )}

              <div>
                <label htmlFor="message" className={`block text-sm font-medium ${themeClasses.text}`}>
                  {feedbackType === 'feedback' ? 'Your Feedback' : 'Why do you recommend this book?'}
                </label>
                <textarea
                  id="message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className={`mt-1 block w-full rounded-md ${themeClasses.border} shadow-sm focus:${themeClasses.highlight} 
                           focus:ring-${themeClasses.primary} sm:text-sm ${themeClasses.dropdown} ${themeClasses.text}`}
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white 
                         ${themeClasses.primary} ${themeClasses.primaryHover} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 
                         ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>

              {submitStatus === 'success' && (
                <p className="text-green-600 text-sm text-center">Thank you for your submission!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-sm text-center">Something went wrong. Please try again.</p>
              )}
            </form>
          )}
        </div>
      </div>
    </div>
  );
}